import React, { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./App.css";

import AppRoutes from "./components/AppRoutes";
import Registration from "./components/Registration";
import Login from "./components/Login";
import Loader from "./components/Loader";
import NotificationBox from "./components/NotificationBox";

import { isAdminExist } from "./redux/slices/adminSlice";
import { getProfileData } from "./redux/slices/profileSlice";
import { setShowMessage } from "./redux/slices/notificationSlice";

import ResetPasswordForm from "./components/ResetPassword";
import NewPasswordForm from "./components/NewPasswordForm";

// A ProtectedRoute wrapper to guard the entire authenticated app
const ProtectedApp = ({ children }) => {
  const rbacToken = localStorage.getItem("rbacToken");
  return rbacToken ? children : <Navigate to="/login" />;
};

function App() {
  const dispatch = useDispatch();
  const { adminExist } = useSelector((state) => state.AdminSlice);
  const notificationState = useSelector(
    (state) => state.notificationState.message
  );
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);

  useEffect(() => {
    // Initial data fetching
    // We use an async function inside useEffect to handle the promise
    const checkAdminStatus = async () => {
      try {
        // Dispatch and wait for the async thunk to complete
        await dispatch(isAdminExist()).unwrap();
      } catch (error) {
        console.error("Failed to check for admin existence:", error);
      } finally {
        // --- CHANGE 2: Once the check is done (success or fail), stop loading ---
        setIsCheckingAdmin(false);
      }
    };

    checkAdminStatus();

    const token = localStorage.getItem("rbacToken");
    if (token) {
      dispatch(getProfileData(token));
    }
  }, [dispatch]);

  useEffect(() => {
    if (notificationState.length > 0) {
      setTimeout(() => {
        dispatch(setShowMessage(""));
      }, 3000);
    }
  }, [notificationState, dispatch]);

  const doesAdminExist = useMemo(() => {
    return adminExist?.result?.result === true;
  }, [adminExist]);

  // --- CHANGE 3: Use the new loading state to show a loader and pause routing ---
  // This prevents the router from rendering before we have the admin status.
  if (isCheckingAdmin) {
    return <Loader />;
  }

  return (
    <>
      <BrowserRouter>
        <Routes>
          {doesAdminExist ? (
            // If admin exists, load the full app router.
            // The /* allows AppRoutes to handle all nested routing.
            <>
              {/* === PUBLIC ROUTES === */}
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPasswordForm />} />
              <Route
                path="/set-new-password/:token"
                element={<NewPasswordForm />}
              />

              {/* === PROTECTED ROUTES === */}
              <Route
                path="/*"
                element={
                  <ProtectedApp>
                    <AppRoutes />
                  </ProtectedApp>
                }
              />
            </>
          ) : (
            // --- INITIAL SETUP FLOW (No Admin Exists) ---
            <>
              <Route path="/registration" element={<Registration />} />
              <Route
                path="*"
                element={<Navigate to="/registration" replace />}
              />
            </>
          )}
        </Routes>
      </BrowserRouter>

      {notificationState && (
        <NotificationBox notificationState={notificationState} />
      )}
    </>
  );
}

export default App;
